/*
 * <story-carousel>
 * ----------------
 * Accessible, dependency-free carousel that enhances an existing scroll
 * container. It is purely additive: it injects prev/next buttons + dots,
 * adds scroll-snap behaviour, ARIA semantics and keyboard support, and
 * auto-hides its controls when all slides already fit (no horizontal
 * overflow — e.g. the grid layout at desktop widths).
 *
 * Markup contract:
 *   <story-carousel aria-label="…">
 *     <div data-carousel-track> … direct children are slides … </div>
 *   </story-carousel>
 *
 * Multiple instances per page are fully supported (everything is scoped to
 * the element). Replaces the previous Slick/jQuery carousels.
 */
if (!customElements.get('story-carousel')) {
  customElements.define(
    'story-carousel',
    class StoryCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        if (this.slides.length === 0) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._lastPage = -1;

        this.#initA11y();
        this.#buildControls();
        this.#bindEvents();
        this.#update();
        this.#initVideoPlayback();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.videoObserver?.disconnect();
        cancelAnimationFrame(this._raf);
      }

      #initA11y() {
        this.setAttribute('role', 'region');
        if (!this.hasAttribute('aria-roledescription')) {
          this.setAttribute('aria-roledescription', 'carousel');
        }
        this.slides.forEach((slide, i) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          if (!slide.hasAttribute('aria-label')) {
            slide.setAttribute('aria-label', `${i + 1} of ${this.slides.length}`);
          }
        });

        this.live = document.createElement('span');
        this.live.className = 'visually-hidden';
        this.live.setAttribute('aria-live', 'polite');
        this.appendChild(this.live);
      }

      #buildControls() {
        const makeButton = (dir, label, points) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `story-carousel__btn story-carousel__btn--${dir}`;
          button.setAttribute('aria-label', label);
          button.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="${points}"></polyline></svg>`;
          button.addEventListener('click', () => this.#page(dir === 'prev' ? -1 : 1));
          return button;
        };

        this.prevBtn = makeButton('prev', 'Previous slide', '15 18 9 12 15 6');
        this.nextBtn = makeButton('next', 'Next slide', '9 18 15 12 9 6');

        this.dotsEl = document.createElement('div');
        this.dotsEl.className = 'story-carousel__dots';
        this.dotsEl.setAttribute('role', 'group');
        this.dotsEl.setAttribute('aria-label', 'Choose slide to display');

        this.append(this.prevBtn, this.nextBtn, this.dotsEl);
      }

      #bindEvents() {
        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(this._raf);
            this._raf = requestAnimationFrame(() => this.#update());
          },
          { passive: true }
        );

        this.addEventListener('keydown', (event) => {
          const tag = event.target.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.#page(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.#page(1);
          }
        });

        this.resizeObserver = new ResizeObserver(() => this.#update());
        this.resizeObserver.observe(this.track);
      }

      // Opt-in (via `data-autoplay-visible`): play the on-screen videos one at a
      // time. The first visible one plays; when it ends the next visible one
      // starts, wrapping round. Playing every visible slide at once streamed ~6
      // videos together on desktop. The markup omits `autoplay` and uses
      // preload="none", so nothing downloads until a video is picked here.
      // A single viewport-rooted observer covers both axes: a scroll container
      // clips its off-screen children, so horizontally scrolled-away slides — and
      // the whole carousel while below the fold — report as not intersecting.
      #initVideoPlayback() {
        if (!this.hasAttribute('data-autoplay-visible') || this.reduceMotion) return;
        this.videos = Array.from(this.track.querySelectorAll('video'));
        if (this.videos.length === 0) return;

        this.visibleVideos = new Set();
        this.activeVideo = null;

        this.videos.forEach((video) => {
          // Advance on `ended` instead of looping. The loop attribute stays in
          // the markup so the videos still loop if this script never runs.
          video.loop = false;
          video.addEventListener('ended', () => this.#playNextVideo(video));
          // A video started from its own controls becomes the one playing.
          video.addEventListener('play', () => {
            this.activeVideo = video;
            this.videos.forEach((other) => {
              if (other !== video && !other.paused) other.pause();
            });
          });
        });

        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                this.visibleVideos.add(video);
              } else {
                this.visibleVideos.delete(video);
                video.pause();
                if (video === this.activeVideo) this.activeVideo = null;
              }
            });
            // Nothing picked (first view, or the playing one scrolled away):
            // start the first visible. A video the shopper paused stays paused.
            if (!this.activeVideo) this.#startVideo(this.#visibleVideosInOrder()[0]);
          },
          { threshold: 0.5 }
        );
        this.videos.forEach((video) => this.videoObserver.observe(video));
      }

      #visibleVideosInOrder() {
        return this.videos.filter((video) => this.visibleVideos.has(video));
      }

      #playNextVideo(current) {
        const visible = this.#visibleVideosInOrder();
        if (visible.length === 0) return;
        const next = visible[(visible.indexOf(current) + 1) % visible.length];
        if (next === current) current.currentTime = 0;
        this.#startVideo(next);
      }

      #startVideo(video) {
        if (!video) return;
        this.activeVideo = video;
        // play() may reject (e.g. data-saver, low-power); ignore — poster and
        // controls remain so the shopper can start it manually.
        video.play?.().catch(() => {});
      }

      get #pageWidth() {
        return this.track.clientWidth || 1;
      }

      get #pageCount() {
        return Math.max(1, Math.round(this.track.scrollWidth / this.#pageWidth));
      }

      get #currentPage() {
        return Math.round(this.track.scrollLeft / this.#pageWidth);
      }

      #page(direction) {
        this.track.scrollBy({
          left: direction * this.#pageWidth,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });
      }

      #goTo(page) {
        this.track.scrollTo({
          left: page * this.#pageWidth,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });
      }

      #renderDots(count, current) {
        if (this.dotsEl.childElementCount !== count) {
          this.dotsEl.replaceChildren();
          for (let i = 0; i < count; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'story-carousel__dot';
            dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
            dot.addEventListener('click', () => this.#goTo(i));
            this.dotsEl.appendChild(dot);
          }
        }
        Array.from(this.dotsEl.children).forEach((dot, i) => {
          dot.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }

      #update() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const hasOverflow = maxScroll > 1;

        this.prevBtn.hidden = !hasOverflow;
        this.nextBtn.hidden = !hasOverflow;
        this.dotsEl.hidden = !hasOverflow;
        this.classList.toggle('story-carousel--has-controls', hasOverflow);
        if (!hasOverflow) return;

        const x = this.track.scrollLeft;
        this.prevBtn.disabled = x <= 1;
        this.nextBtn.disabled = x >= maxScroll - 1;

        const count = this.#pageCount;
        const current = Math.min(Math.max(this.#currentPage, 0), count - 1);
        this.#renderDots(count, current);

        if (this._lastPage !== current) {
          this._lastPage = current;
          this.live.textContent = `Slide ${current + 1} of ${count}`;
        }
      }
    }
  );
}
